import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function Hero() {
  return (
    <section
      data-testid="hero"
      className="relative w-full h-[100svh] min-h-[700px] overflow-hidden bg-[color:var(--he-ink)]"
    >
      {/* Background image with strong overlays */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1612690207805-a3c172ea6e8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt="Áramló haj — Hair Elixir"
          className="w-full h-full object-cover he-ken-burns"
        />
        {/* Heavy left-darkening so text is always legible */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/15" />
        {/* Vertical depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-transparent to-black/70" />
      </div>

      {/* Grid layout: top masthead / main content / bottom marquee */}
      <div className="relative z-10 h-full grid grid-rows-[auto_1fr_auto]">
        {/* TOP — Masthead */}
        <div className="pt-28 md:pt-32">
          <div className="he-container">
            <div className="flex items-center justify-between text-white/85">
              <span
                className="overline-xs he-rise"
                style={{ animationDelay: "150ms", letterSpacing: "0.34em" }}
              >
                Új kollekció · 2026
              </span>
              <span className="hidden sm:block h-px flex-1 mx-8 bg-[color:var(--he-gold)]/40" />
              <span
                className="overline-xs he-rise"
                style={{ animationDelay: "250ms", letterSpacing: "0.34em" }}
              >
                Ingyenes szállítás 100 000 Ft felett
              </span>
            </div>
          </div>
        </div>

        {/* MIDDLE — Main content (vertically end-aligned for editorial feel) */}
        <div className="flex items-end pb-12 md:pb-14">
          <div className="he-container w-full">
            <div className="grid grid-cols-12 gap-8 items-end">
              {/* Left: headline + CTA */}
              <div className="col-span-12 lg:col-span-9">
                {/* Eyebrow */}
                <div
                  className="flex items-center gap-5 mb-7 he-rise"
                  style={{ animationDelay: "350ms" }}
                >
                  <span className="block w-12 h-px bg-[color:var(--he-gold)]" />
                  <span
                    className="text-[color:var(--he-gold)] uppercase text-xs"
                    style={{ letterSpacing: "0.34em", fontWeight: 500 }}
                  >
                    100% emberi haj · Európai és kazahsztáni
                  </span>
                </div>

                {/* Headline */}
                <h1
                  className="font-serif-display text-white leading-[0.94] tracking-[-0.022em] he-rise"
                  style={{
                    animationDelay: "500ms",
                    fontWeight: 400,
                    fontSize: "clamp(2.8rem, 6.8vw, 6.8rem)",
                    textShadow: "0 4px 60px rgba(0,0,0,0.65)",
                  }}
                >
                  A haj, amit{" "}
                  <span className="italic" style={{ fontWeight: 300 }}>
                    megérdemel.
                  </span>
                </h1>

                {/* Tagline */}
                <p
                  className="mt-7 max-w-xl text-white font-light text-base md:text-lg leading-[1.6] he-rise"
                  style={{
                    animationDelay: "750ms",
                    textShadow: "0 2px 20px rgba(0,0,0,0.55)",
                  }}
                >
                  Prémium póthajak széles választéka, melyeket 12 év
                  szakmai tapasztalattal és maximális odafigyeléssel
                  egyenként válogatva hozunk nektek. A beszerzés európai
                  régiókból és Kazahsztánból történik.
                </p>

                {/* CTAs */}
                <div
                  className="mt-9 flex flex-col sm:flex-row gap-7 sm:items-center he-rise"
                  style={{ animationDelay: "950ms" }}
                >
                  <Link
                    to="/termekek"
                    data-testid="hero-cta-collection"
                    className="btn-arrow text-white"
                  >
                    <span className="arrow-circle">
                      <ArrowRight size={18} className="arrow-icon text-white" />
                    </span>
                    <span style={{ fontWeight: 500 }}>Kollekció felfedezése</span>
                  </Link>
                  <span className="hidden sm:block w-10 h-px bg-white/30" />
                  <Link
                    to="/kerdoiv"
                    data-testid="hero-link-quiz"
                    className="btn-ghost text-white"
                    style={{ fontWeight: 500, opacity: 0.85 }}
                  >
                    <span>Hajtípus választó</span>
                    <ArrowRight size={14} className="ghost-arrow" />
                  </Link>
                </div>
              </div>

              {/* Right: edition mark */}
              <div
                className="hidden lg:flex col-span-3 flex-col items-end gap-3 pb-2 he-rise"
                style={{ animationDelay: "1100ms" }}
              >
                <span className="figure italic text-white/40 text-[5rem] leading-none">
                  11
                </span>
                <span
                  className="overline-xs text-white/65 text-right max-w-[200px]"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.32em" }}
                >
                  tétel az aktuális <br /> kollekcióban
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM — Marquee in own row */}
        <div className="border-t border-[color:var(--he-gold)]/30 bg-black/65 backdrop-blur-md overflow-hidden">
          <div className="flex he-marquee whitespace-nowrap py-3.5">
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex shrink-0">
                {[
                  "100% emberi haj",
                  "Európai forrás",
                  "Kazahsztáni forrás",
                  "Festhető",
                  "Megfelelő ápolás mellett évekig hordható",
                ].map((t, i) => (
                  <span
                    key={i}
                    className="text-white/85 px-9 inline-flex items-center gap-9 shrink-0 uppercase"
                    style={{
                      fontFamily: "Jost, sans-serif",
                      fontSize: "0.66rem",
                      letterSpacing: "0.3em",
                      fontWeight: 400,
                    }}
                  >
                    {t}
                    <span className="text-[color:var(--he-gold)]">✦</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
